import React, { useState, useEffect, useRef } from "react";
import { getInsuranceProvidersForState } from "../../config/insuranceProviders";

const PaymentQuestion = ({ onSubmit, onNext, initialValue, selectedState }) => {
  // initialValue may be an object like { type: 'insurance', provider: 'Aetna' } or { type: 'self-pay' }
  const [selectedPayment, setSelectedPayment] = useState(initialValue || null);
  const [dividerType, setDividerType] = useState('vertical'); // 'vertical' or 'horizontal'
  const continueButtonRef = useRef(null);
  const lastInsuranceButtonRef = useRef(null);
  const outOfPocketButtonRef = useRef(null);
  const dividerRef = useRef(null);

  // Providers for state (strings) - defined early to use in useEffect dependencies
  const insuranceProviders = selectedState ? getInsuranceProvidersForState(selectedState) : [];

  useEffect(() => {
    if (initialValue) setSelectedPayment(initialValue);
  }, [initialValue]);

  // Auto-scroll to continue button when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (continueButtonRef.current) {
        continueButtonRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Detect if "Out of pocket" is on a new row
  useEffect(() => {
    const checkDividerType = () => {
      // If no insurance providers, use horizontal divider
      if (insuranceProviders.length === 0) {
        setDividerType('horizontal');
        return;
      }
      
      if (lastInsuranceButtonRef.current && outOfPocketButtonRef.current) {
        const lastInsuranceRect = lastInsuranceButtonRef.current.getBoundingClientRect();
        const outOfPocketRect = outOfPocketButtonRef.current.getBoundingClientRect();
        const lastInsuranceTop = lastInsuranceRect.top;
        const outOfPocketTop = outOfPocketRect.top;
        const threshold = 15; // Allow some tolerance for positioning
        
        // Check if they're on different rows by comparing top positions
        const isNewRow = Math.abs(lastInsuranceTop - outOfPocketTop) > threshold;
        setDividerType(isNewRow ? 'horizontal' : 'vertical');
      } else {
        // If refs aren't ready, wait a bit and try again
        setTimeout(checkDividerType, 100);
      }
    };

    // Check on mount with multiple delays to ensure layout is complete
    const timers = [
      setTimeout(checkDividerType, 50),
      setTimeout(checkDividerType, 150),
      setTimeout(checkDividerType, 300),
    ];
    
    window.addEventListener('resize', checkDividerType);
    
    return () => {
      window.removeEventListener('resize', checkDividerType);
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [selectedState, insuranceProviders.length]);

  // Click handlers normalize the payload
  const chooseInsurance = (provider) => {
    const value = { type: "insurance", provider };
    setSelectedPayment(value);
  };

  const chooseSelfPay = () => {
    const value = { type: "self-pay" };
    setSelectedPayment(value);
  };

  const handleContinue = () => {
    if (selectedPayment) {
      onSubmit(selectedPayment);
      onNext();
    }
  };

  if (!selectedState) {
    return (
      <div className="payment-placeholder">
        <p>Please select your state first to see available insurance options.</p>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => e.preventDefault()} className="input-form">
      <div className="payment-options-container">
        <div className="payment-options">
          {insuranceProviders.map((p, index) => (
            <button
              key={p}
              ref={index === insuranceProviders.length - 1 ? lastInsuranceButtonRef : null}
              type="button"
              className={`payment-button ${selectedPayment?.type === "insurance" && selectedPayment?.provider === p
                ? "payment-button-selected"
                : ""
                }`}
              onClick={() => chooseInsurance(p)}
            >
              {p}
            </button>
          ))}

          <div 
            ref={dividerRef}
            className={`payment-divider payment-divider-${dividerType}`}
          />

          <button
            ref={outOfPocketButtonRef}
            type="button"
            className={`payment-button ${selectedPayment?.type === "self-pay" ? "payment-button-selected" : ""
              }`}
            onClick={chooseSelfPay}
          >
            Out of pocket
          </button>
        </div>
        
        <button
          ref={continueButtonRef}
          type="button"
          className="payment-submit-button"
          onClick={handleContinue}
          disabled={!selectedPayment}
        >
          Continue
        </button>
      </div>
    </form>
  );
};

export default PaymentQuestion;
